import Button, { ButtonVariant } from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import cx from 'classnames';
import { ReactNode } from 'react';

import testingConstants from 'utils/testingConstants';
import { IconType } from 'utils/types';

const footerButtonClass = 'w-full md:w-fit';

type BaseModalFooterProps = {
  cancelText: string;
  successButtonText: string;
  successButtonIcon?: IconType;
  successButtonIconPosition?: 'before' | 'after';
  onCloseClick: () => void;
  onSubmit: () => void;
  successDisabled?: boolean;
  isLoading?: boolean;
  isCancelButtonHidden?: boolean;
  isCancelDisabled?: boolean;
  successButtonVariant?: ButtonVariant;
  className?: string;
  hasBorder?: boolean;
  deleteLayout?: boolean;
};

type ModalFooterPropsWithLeftTextButton = BaseModalFooterProps & {
  leftSideButtonText: string;
  onLeftSideButtonClick: () => void;
  leftSideButton?: never;
};

type ModalFooterPropsWithLeftComponentButton = BaseModalFooterProps & {
  leftSideButton: ReactNode;
  leftSideButtonText?: never;
  onLeftSideButtonClick?: never;
};

type ModalFooterPropsWithoutLeftButton = BaseModalFooterProps & {
  leftSideButtonText?: never;
  onLeftSideButtonClick?: never;
  leftSideButton?: never;
};

type ModalFooterProps =
  | ModalFooterPropsWithLeftTextButton
  | ModalFooterPropsWithLeftComponentButton
  | ModalFooterPropsWithoutLeftButton;

const ModalFooter = ({
  cancelText,
  successButtonText,
  successButtonIcon: Icon,
  successButtonIconPosition = 'before',
  onCloseClick,
  onSubmit,
  successDisabled = false,
  isLoading = false,
  isCancelButtonHidden = false,
  isCancelDisabled = false,
  successButtonVariant = 'primary',
  leftSideButtonText,
  onLeftSideButtonClick,
  leftSideButton,
  className,
  hasBorder = true,
  deleteLayout = false,
}: ModalFooterProps) => {
  const hasLeftSideButtonWithText = Boolean(leftSideButtonText && onLeftSideButtonClick);
  const hasDeleteLayout = deleteLayout && !!leftSideButton;

  // Button width:
  // - deleteLayout mobile: flex-1 so Cancel+Save share the row equally (w-1/2 + gap-4 exceeds 100%).
  // - deleteLayout desktop: sm:flex-none sm:w-fit reverts to content-sized buttons.
  // - normal path: w-full on mobile, md:w-fit on desktop.
  const actionButtonClass = hasDeleteLayout ? 'flex-1 sm:flex-none sm:w-fit' : footerButtonClass;

  return (
    <Popup.Footer
      wrapperClassName="!pb-4 !px-0"
      className={cx('w-full !pt-4 !px-4 flex-wrap sm:flex-nowrap justify-center sm:justify-end', {
        '!border-none': !hasBorder,
      })}
    >
      {leftSideButton && (
        <div
          className={cx('w-full', {
            // Default: margin below the button separates it from Cancel+Save on mobile.
            'mb-4 sm:mb-0': !hasDeleteLayout,
            // deleteLayout: push below Cancel+Save on mobile; gap comes from mt-3 (not mb).
            // On desktop sm:order-none keeps it left of Cancel+Save; sm:mt-0 removes the gap.
            'mt-3 sm:mt-0 order-last sm:order-none': hasDeleteLayout,
          })}
        >
          {leftSideButton}
        </div>
      )}
      {hasLeftSideButtonWithText && (
        <div className="w-full">
          <Button
            className="!w-full !py-[11px] sm:!w-fit text-primary-50"
            variant="textSecondary"
            onClick={onLeftSideButtonClick}
          >
            {leftSideButtonText}
          </Button>
        </div>
      )}
      <div
        className={cx(
          'w-full flex gap-4 md:w-fit',
          { 'flex-wrap sm:flex-nowrap': hasLeftSideButtonWithText || leftSideButton },
          // On mobile bring Cancel+Save above the delete button.
          { 'order-first sm:order-none': hasDeleteLayout },
          // deleteLayout: shrink buttons div to content width at sm: to match the
          // sm:flex-nowrap breakpoint, preventing a gap in the 640–768px range.
          { 'sm:w-fit': hasDeleteLayout },
          className
        )}
      >
        {!isCancelButtonHidden && (
          <Button
            variant="tertiary"
            className={actionButtonClass}
            onClick={onCloseClick}
            disabled={isCancelDisabled}
            data-testid={testingConstants.cancelFormButton}
          >
            {cancelText}
          </Button>
        )}
        <Button
          variant={successButtonVariant}
          onClick={onSubmit}
          loading={isLoading}
          disabled={successDisabled || isLoading}
          className={cx('capitalize group', actionButtonClass)}
          data-testid={testingConstants.submitFormButton}
        >
          <div
            className={cx('flex items-center gap-3', {
              'flex-row-reverse': successButtonIconPosition === 'after',
            })}
          >
            {Icon && <Icon className="w-6 h-6 stroke-1.5 stroke-white group-disabled:stroke-gray-50" />}
            {successButtonText}
          </div>
        </Button>
      </div>
    </Popup.Footer>
  );
};

export default ModalFooter;
